import { BehaviorSubject, Observable, Subject } from "rxjs";
import { IWsMessage, IWsSubscription, isIWsDataMessage, isIWsMessage } from "./api.types";

function debug(message: string) {
  if (process.env["NODE_ENV"] === "development") {
    console.log(`[Api] DEBUG ${message}`)
  }
}

function error(message: string) {
  console.error(`[Api] ERROR ${message}`)
}

class Api {
  private SOCKET!: WebSocket
  private SUBSCRIPTIONS: {[topic: string]: IWsSubscription} = {}
  private MESSAGE_BUS = new Subject<IWsMessage>()
  private STATUS_BUS = new BehaviorSubject("setup")

  constructor() {
    this.MESSAGE_BUS.subscribe((msg: IWsMessage) => {
      const topic = msg.topic.split("/")
      const type = topic[0]
      const service = topic.slice(1).join("/")
      if (type === "data" && this.SUBSCRIPTIONS.hasOwnProperty(service)) {
        if (isIWsDataMessage(msg)) {
          this.SUBSCRIPTIONS[service].data.next(msg.data)
        }
      }
    })
    this.connect()
  }

  private connect() {
    debug("Connecting...")
    this.STATUS_BUS.next("connecting")
    let url = "/"
    if (process.env.VUE_APP_SERVER_URL) {
      url = process.env.VUE_APP_SERVER_URL
    } else {
      url = [window.location.origin.replace(/^http/, "ws"), window.location.pathname, "api/"].join("/").replaceAll(/(?<!:)\/\/+/g, "/");
    }

    this.SOCKET = new WebSocket(url)
    this.SOCKET.onmessage = (msg_raw: MessageEvent<string>) => {
      let msg;
      try {
        msg = JSON.parse(msg_raw.data)
      } catch {
        error("Failed to parse websocket message")
      }

      if (isIWsMessage(msg)) {
        this.MESSAGE_BUS.next(msg)
      }
    }

    this.SOCKET.onopen = () => {
      debug("Connected.")
      this.STATUS_BUS.next("connected")
      Object.keys(this.SUBSCRIPTIONS).forEach(service => {
        this.sendResponse(`subscribe/${service}`, "")
          .subscribe({
            next: (data: any) => {
              debug(data)
            },
            error: (error: Error) => {
              debug(error.toString())
            }
        })
      })
    }

    this.SOCKET.onerror = (ev) => {
      if (this.STATUS_BUS.getValue() === "connected") {
        error(`socket error: ${JSON.stringify(ev)}`)
        this.STATUS_BUS.next("error")
      }
      this.SOCKET.close()
    }

    this.SOCKET.onclose = () => {
      if (this.STATUS_BUS.getValue() !== "setup") {
        debug("Disconnected.")
        this.STATUS_BUS.next("disconnected")
      }

      setTimeout(() => {
        this.connect()
      }, 3000)
    }
  }

  subscribe(service: string) {
    return new Promise<BehaviorSubject<any>>((resolve, reject) => {
      if (!this.SUBSCRIPTIONS.hasOwnProperty(service)) {
        this.SUBSCRIPTIONS[service] = {
          subscribers: 1,
          data: new BehaviorSubject(null)
        }
        this.sendResponse(`subscribe/${service}`, "")
          .subscribe({
            next: (data: any) => {
              debug(data)
              resolve(this.SUBSCRIPTIONS[service].data)
            },
            error: (error) => {
              debug(error)
              resolve(this.SUBSCRIPTIONS[service].data)
            }
        })
      } else {
        this.SUBSCRIPTIONS[service].subscribers++
        resolve(this.SUBSCRIPTIONS[service].data)
      }
    })
  }

  unsubscribe(service: string) {
    return new Promise<void>((resolve, reject) => {
      if (this.SUBSCRIPTIONS.hasOwnProperty(service)) {
        this.SUBSCRIPTIONS[service].subscribers--
        if (this.SUBSCRIPTIONS[service].subscribers <= 0) {
          this.SUBSCRIPTIONS[service].data.complete()
          delete this.SUBSCRIPTIONS[service]
          this.sendResponse(`unsubscribe/${service}`, "")
            .subscribe({
              next: (data) => {
                debug(data)
                resolve()
              },
              error: (error) => {
                debug(error)
                reject(error)
              }
            })
        }
      }
    })
  }

  sendResponse(topic: string, data: any){
    return new Observable<any>((subscriber) => {
      let msgSub = this.MESSAGE_BUS.subscribe(msg => {
        if (msg.topic === topic) {
          msgSub.unsubscribe()
          if (isIWsDataMessage(msg)) {
            subscriber.next(msg.data)
          } else {
            subscriber.error(msg.error)
          }
          subscriber.complete()
        }
      })
      this.send(topic, data)
    })
  }

  status() {
    return this.STATUS_BUS
  }

  private send(topic: string, data: any) {
    let msg = {
      topic,
      data
    }
    if (this.SOCKET.readyState == this.SOCKET.OPEN) {
      this.SOCKET.send(JSON.stringify(msg))
    }
  }
}

export let api = new Api()
